import dayjs from "dayjs";
import type {
  LogItemType,
  LogAttributesType,
  Change,
} from "@/types/common/logs";
import CircleLine from "./circle-line";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Icon,
} from "@elaniintc/goes-design-system";
import { FORM_ASSIGNMENTS_STATUS } from "@/types/entities/formality-request";
import { useIntersectionObserver } from "usehooks-ts";
import { useEffect } from "react";

interface LogsProps {
  title: string;
  items: LogItemType[];
  emptyText?: string;
  isLoading?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
}

export default function Logs({
  title,
  items,
  emptyText,
  isLoading = false,
  hasNextPage = false,
  fetchNextPage,
}: LogsProps) {
  const formatDate = (date: Date) => {
    return dayjs(date).format("MMM D, YYYY | HH:mm");
  };

  const getChangeTitle = (
    changeName: string,
    subject_type?: string,
  ): string => {
    let titles: Record<string, string> = {
      name: "Título de proceso actualizado",
      description: "Descripción de proceso actualizada",
      file_name: "Ficha técnica actualizada",
      status: "Estatus de proceso actualizado",
      tags: "Etiqueta de proceso actualizada",
      // Tag-specific changes
      tag_name: "Nombre de etiqueta actualizado",
      tag_description: "Descripción de etiqueta actualizada",
      tag_status: "Estado de etiqueta actualizado",
      tag_image: "Imagen de etiqueta actualizada",
      permissions: "Permisos del rol actualizados",
    };
    if (subject_type === "expedient_base_entity") {
      titles = {
        name: "Nombre de entidad actualizado",
        description: "Descripción de entidad actualizada",
        status: "Estatus de entidad actualizado",
        tags: "Etiqueta de entidad actualizada",
        // Tag-specific changes
      };
    }
    return titles[changeName] || "Cambio de proceso";
  };

  const actionTranslator = (action: string) => {
    if (!action) {
      return;
    }
    if (action === "created") {
      return "creado";
    }
    if (action === "updated") {
      return "actualizado";
    }
    if (action === "deleted") {
      return "desactivado";
    }
    if (action === "restored") {
      return "reactivado";
    }
    if (action === "activated") {
      return "activado";
    }
    if (action === "deactivated") {
      return "desactivado";
    }
    if (action === "process_duplicated") {
      return "duplicado";
    }
    if (action === "users_assigned") {
      return "Usuarios asignados";
    }
    if (action === "users_unassigned") {
      return "Usuarios desasignados";
    }
    if (action === "permissions_sync" || action === "permissions_updated") {
      return "Permisos actualizados";
    }
  };

  const logTypeTranslator = (type: string) => {
    if (!type) {
      return;
    }
    if (type === "process") {
      return "Proceso";
    }
    if (type === "flow") {
      return "Flujo";
    }
    if (type === "tag") {
      return "Etiqueta";
    }
    if (type === "expedient_base_entity") {
      return "Entidad base de expediente";
    }
    if (type === "user" || type === "applicant") {
      return "Usuario";
    } else {
      return type;
    }
  };

  function dateToUtc(input: Date | string): string {
    const date = new Date(input);

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000000Z`;
  }

  const logMessageValidation = (logData: LogItemType) => {
    if (logData?.action === "deleted") {
      const states = logData?.subject?.user_states || [];
      for (let i = 0; i < states.length; i++) {
        const state = logData.subject?.user_states?.[i];
        if (!state) continue;
        const logDateUTC = new Date(dateToUtc(logData?.date)).getTime();
        const stateDateUTC = new Date(state.created_at).getTime();

        if (logDateUTC === stateDateUTC) {
          return state.description;
        }
      }
    }

    return logData.description;
  };

  const Description = ({ log }: { log: LogItemType }) => {
    const changes = getChanges(
      log.properties?.old as LogAttributesType,
      log.properties?.attributes as LogAttributesType,
    );

    const statusChange = changes.find((c) => c.name === "status");
    const isArchived = statusChange?.current === "INACTIVE";

    // Verificar fechas programadas independientemente del cambio de estado
    const scheduledFrom = log.properties?.attributes?.scheduled_inactive_from;
    const scheduledTo = log.properties?.attributes?.scheduled_inactive_to;

    // Si hay fechas programadas, mostrarlas primero
    if (scheduledFrom && scheduledTo) {
      return (
        <h3 className="mb-2">
          Se programó la inactivación del proceso desde{" "}
          {dayjs(scheduledFrom).format("DD/MM/YYYY [a las] HH:mm")} hasta{" "}
          {dayjs(scheduledTo).format("DD/MM/YYYY [a las] HH:mm")}
        </h3>
      );
    }

    if (isArchived) {
      const from = log.properties?.attributes?.inactive_from;
      const to = log.properties?.attributes?.inactive_to;

      if (from && to) {
        return (
          <h3 className="mb-2">
            Se archivó el proceso desde {dayjs(from).format("DD/MM/YYYY")} hasta{" "}
            {dayjs(to).format("DD/MM/YYYY")}
          </h3>
        );
      }

      return <h3 className="mb-2">Se archivó el proceso</h3>;
    }

    if (changes.length) {
      const titles = [
        ...new Set(
          changes.map((c) => getChangeTitle(c.name, log.subject_type)),
        ),
      ];
      return <h3 className="mb-2">{titles.join(", ")}</h3>;
    }

    if (log.subject_type || log.action) {
      const translatedAction = actionTranslator(log.action);
      console.log(log, translatedAction);
      if (translatedAction) {
        return (
          <h3 className="mb-2">
            {log.subject_type && `${logTypeTranslator(log.subject_type)} `}
            {translatedAction}
            {": "} {logMessageValidation(log)}
          </h3>
        );
      }
      // Fallback if action is not translatable but description exists
      if (log.description) {
        return <h3 className="mb-2">{log.description}</h3>;
      }
    }

    return <h3 className="mb-2">{log.description}</h3>;
  };

  const statusTraducer = (
    status:
      | keyof typeof FORM_ASSIGNMENTS_STATUS
      | "ON_CONSTRUCTION"
      | "ACTIVE"
      | "INACTIVE"
      | "PUBLISHED"
      | "APPROVED"
      | "DRAFT"
      | "IN_PROCESS"
      | "FORCE_COMPLETED",
  ) => {
    switch (status) {
      case FORM_ASSIGNMENTS_STATUS.SUBMITTED:
        return "Enviado";
      case FORM_ASSIGNMENTS_STATUS.COMPLETED:
        return "Completado";
      case FORM_ASSIGNMENTS_STATUS.IN_PROGRESS:
        return "En progreso";
      case FORM_ASSIGNMENTS_STATUS.CORRECTION:
        return "Revisión";
      case FORM_ASSIGNMENTS_STATUS.FILL_CORRECTION:
        return "Observado";
      case FORM_ASSIGNMENTS_STATUS.ASSIGNED:
        return "Pendiente";
      case FORM_ASSIGNMENTS_STATUS.FAILED_CORRECTION:
        return "Rechazado";
      case "ON_CONSTRUCTION":
        return "En construcción";
      case "APPROVED":
        return "Aprobado";
      case "PUBLISHED":
        return "Publicado";
      case "ACTIVE":
        return "Activo";
      case "INACTIVE":
        return "Archivado";
      case "DRAFT":
        return "Borrador";
      case "IN_PROCESS":
        return "En proceso";
      case "FORCE_COMPLETED":
        return "Completado forzosamente";
      default:
        return "-";
    }
  };

  const statusColor = (
    status: string,
  ): "info" | "success" | "warning" | "neutral" | "danger" => {
    switch (status) {
      case FORM_ASSIGNMENTS_STATUS.SUBMITTED:
        return "info";
      case FORM_ASSIGNMENTS_STATUS.COMPLETED:
        return "success";
      case FORM_ASSIGNMENTS_STATUS.IN_PROGRESS:
        return "info";
      case FORM_ASSIGNMENTS_STATUS.CORRECTION:
        return "info";
      case FORM_ASSIGNMENTS_STATUS.FILL_CORRECTION:
        return "warning";
      case FORM_ASSIGNMENTS_STATUS.ASSIGNED:
        return "neutral";
      case FORM_ASSIGNMENTS_STATUS.FAILED_CORRECTION:
        return "danger";
      case "ON_CONSTRUCTION":
        return "info";
      case "APPROVED":
        return "success";
      case "PUBLISHED":
        return "success";
      case "ACTIVE":
        return "success";
      case "INACTIVE":
        return "warning";
      case "DRAFT":
        return "neutral";
      case "IN_PROCESS":
        return "info";
      case "FORCE_COMPLETED":
        return "success";
      default:
        return "neutral";
    }
  };

  function getChanges(
    oldData: LogAttributesType,
    currentData: LogAttributesType,
  ): Change[] {
    if (!oldData || !currentData) return [];

    const allowedKeys: (keyof LogAttributesType)[] = [
      "name",
      "description",
      "file_name",
      "status",
      "tags",
      "active", // Tag-specific field
      "permissions",
    ];

    const changes: Change[] = [];

    for (const key in oldData) {
      const typedKey = key as keyof LogAttributesType;

      if (!allowedKeys.includes(typedKey)) continue;

      const oldValue = oldData[typedKey];
      const newValue = currentData[typedKey];

      const isDifferent =
        Array.isArray(oldValue) && Array.isArray(newValue)
          ? JSON.stringify(oldValue) !== JSON.stringify(newValue)
          : oldValue !== newValue;

      if (isDifferent) {
        changes.push({
          key: typedKey,
          name: key,
          previous: oldValue,
          current: newValue,
        });
      }
    }

    return changes;
  }

  const Banner = ({ change }: { change: Change }) => {
    const renderTextChange = () => (
      <>
        <h3 className="line-through text-on-disabled">{change.previous}</h3>
        <h3 className="text-body">{change.current}</h3>
      </>
    );

    const renderBadgeChange = (
      prev: React.ReactNode,
      next: React.ReactNode,
      prevVariant: "info" | "success" | "warning" | "neutral" | "danger",
      nextVariant: "info" | "success" | "warning" | "neutral" | "danger",
    ) => (
      <div className="flex items-center gap-2">
        <span className="text-sm text-body">De</span>
        <Badge size="xs" variant={prevVariant}>
          {prev}
        </Badge>
        <span className="text-sm text-body">a</span>
        <Badge size="xs" variant={nextVariant}>
          {next}
        </Badge>
      </div>
    );

    const renderTagsChange = () =>
      renderBadgeChange(
        <>
          {typeof change.previous === "string" && change.previous}
          <Icon type="fas" name="edit" className="text-warning-quiet text-xs" />
        </>,
        <>
          {typeof change.current === "string" && change.current}
          <Icon
            type="fas"
            name="check"
            className="text-success-quiet text-xs"
          />
        </>,
        "warning",
        "success",
      );

    const renderStatusChange = () =>
      renderBadgeChange(
        statusTraducer(change.previous as keyof typeof FORM_ASSIGNMENTS_STATUS),
        statusTraducer(change.current as keyof typeof FORM_ASSIGNMENTS_STATUS),
        statusColor(change.previous as string),
        statusColor(change.current as string),
      );

    const renderActiveChange = () =>
      renderBadgeChange(
        change.previous ? "Activa" : "Inactiva",
        change.current ? "Activa" : "Inactiva",
        change.previous ? "success" : "neutral",
        change.current ? "success" : "neutral",
      );

    const renderFileChange = () => (
      <div className="flex items-center gap-2">
        <Badge size="xs" variant="disabled">
          <span className="text-sm text-on-disabled">{change.previous}</span>
          <Icon type="far" name="file" />
        </Badge>
        <Icon type="far" name="arrow-right" />
        <Badge size="xs" variant="neutral">
          <span className="text-sm">{change.current}</span>
          <Icon type="far" name="file" />
        </Badge>
      </div>
    );

    const renderPermissionsChange = () => {
      const prev = (
        Array.isArray(change.previous) ? change.previous : []
      ) as string[];
      const curr = (
        Array.isArray(change.current) ? change.current : []
      ) as string[];

      const added = curr.filter((p) => !prev.includes(p));
      const removed = prev.filter((p) => !curr.includes(p));

      return (
        <div className="flex flex-col gap-2 w-full">
          {added.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-body-quiet">Agregados</span>
              <div className="flex flex-wrap gap-1">
                {added.map((p) => (
                  <Badge key={`add-${p}`} size="xs" variant="success">
                    {p}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {removed.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-body-quiet">Eliminados</span>
              <div className="flex flex-wrap gap-1">
                {removed.map((p) => (
                  <Badge key={`rem-${p}`} size="xs" variant="danger">
                    {p}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="flex flex-col min-h-11 mb-2 p-2 gap-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
        {["name", "description"].includes(change.name) && renderTextChange()}
        {change.name === "tags" && renderTagsChange()}
        {change.name === "status" && renderStatusChange()}
        {change.name === "active" && renderActiveChange()}
        {change.name === "file_name" && renderFileChange()}
        {change.name === "permissions" && renderPermissionsChange()}
      </div>
    );
  };

  const BannerChanges = ({ log }: { log: LogItemType }) => {
    const changes = getChanges(
      log.properties?.old as LogAttributesType,
      log.properties?.attributes as LogAttributesType,
    );

    const statusChange = changes.find((c) => c.name === "status");
    const isArchived = statusChange?.current === "INACTIVE";
    const isUnarchived =
      statusChange?.previous === "INACTIVE" &&
      statusChange?.current !== "INACTIVE";

    if ((isArchived || isUnarchived) && statusChange) {
      return (
        <div className="flex flex-col min-h-11 mb-2 p-2 gap-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
          <div className="flex items-center gap-2">
            <span className="text-sm text-body">De</span>
            <Badge size="xs" variant={statusColor(statusChange.previous)}>
              {statusTraducer(statusChange.previous)}
            </Badge>
            <span className="text-sm text-body">a</span>
            <Badge size="xs" variant={statusColor(statusChange.current)}>
              {statusTraducer(statusChange.current)}
            </Badge>
          </div>

          {log.properties?.attributes?.reason && (
            <div className="flex flex-col gap-1 mt-1">
              <div className="flex items-center gap-2">
                <Icon
                  type="fas"
                  name="comment"
                  className={isArchived ? "text-warning" : "text-success"}
                />
                <span
                  className={`text-sm font-semibold ${isArchived ? "text-warning" : "text-success"}`}
                >
                  {isArchived
                    ? "Motivo de la archivación:"
                    : "Motivo de la reactivación:"}
                </span>
              </div>
              <span className="text-sm text-body">
                {log.properties.attributes.reason}
              </span>
            </div>
          )}
        </div>
      );
    }

    return (
      <>
        {changes.map((changeItem) => (
          <Banner key={changeItem.key} change={changeItem as Change} />
        ))}
      </>
    );
  };

  const [ref, isIntercepting, entry] = useIntersectionObserver({});

  useEffect(() => {
    if (isIntercepting && !isLoading && entry?.isIntersecting && hasNextPage) {
      fetchNextPage?.();
    }
  }, [isIntercepting, hasNextPage, entry, isLoading, fetchNextPage]);

  return (
    <div className="flex flex-col w-full h-full leading-7 text-body font-normal text-base">
      {items.length > 0 && <h2 className="mb-6">{title}</h2>}
      {/* Timeline Container */}
      {items.length !== 0 && (
        <div className="relative">
          {items.map((log, index) => {
            const isLast = index === items.length - 1;
            return (
              <div
                key={`log-${log.id}-${index}`}
                className={cn("flex gap-3 -mt-2", !isLast && "mb-0")}
              >
                <CircleLine
                  option={index === 0 ? "init" : isLast ? "end" : "middle"}
                  showLine={!isLast}
                />
                <div className={cn("flex-1", !isLast && "pb-6")}>
                  {/* Description */}
                  <Description log={log} />
                  {/* Details */}
                  {log.properties?.attributes && log.properties?.old && (
                    <BannerChanges log={log} />
                  )}
                  {/* Author */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-body">Por</span>
                    <Avatar className="shrink-0" size="xs">
                      <AvatarFallback color="action">
                        {log.author.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-action">{log.author}</span>
                  </div>

                  {/* Date */}
                  <div className="text-sm text-body-quiet capitalize">
                    {formatDate(log.date)}
                  </div>
                </div>
              </div>
            );
          })}
          {hasNextPage && (
            <div
              className="w-full flex items-center justify-center h-1"
              ref={ref}
            />
          )}
        </div>
      )}
      {items.length === 0 && (
        <div className="flex-1 text-center text-body-quiet flex items-center justify-center ">
          {emptyText || "No hay actividades recientes"}
        </div>
      )}
    </div>
  );
}
